import type {
  TYearPeriod,
  TDifficulty,
  TChildrenGrade,
  TAvailablePlatform
} from './common'

// A rendszerben megtalálható címkék típusai.
// Korábban ez nem volt típusos, hanem dinamikusan lehetett
// felvenni, módosítani és törölni. Mivel ezekre a funkciókra nem
// igazán van/volt szükség – csak egy felesleges indirekciót hozott –
// ezért kerültek át ezek ide és legalább így fixen típusbiztos lesz.
export const LABEL_TYPES = [
  'gameType',
  'developmentArea',
  'theme',
  'subject',
  'educationalMaterial',
  'pedagogicalCategory',
  'gradePeriod',
  'gradeDifficulty'
] as const

export type TLabelType = typeof LABEL_TYPES[number]

/** Meghatározza, hogy az egyes címketípusoknak típusú „szülője” lehet.  */
export const POSSIBLE_LABEL_PARENT_TYPES: Record<TLabelType, TLabelType[]> = {
  developmentArea: [],
  educationalMaterial: [ 'educationalMaterial', 'subject' ],
  gameType: [],
  subject: [ 'subject' ],
  theme: [],
  pedagogicalCategory: [],
  gradeDifficulty: [],
  gradePeriod: []
}

export type TTranslationMap = TReadonlyRecord<TAvailablePlatform, string>

export type TLabelTranslation = TTranslationMap

export interface IBaseThemeLabelCount {
  readonly count: number
  readonly labelId: number
}

export interface IThemeLabelCount extends IBaseThemeLabelCount {
  readonly moduleId: number
}

export type TThemeLabelCounts = readonly IThemeLabelCount[]

export interface IRouteThemeLabelCount extends IBaseThemeLabelCount {
  readonly routeId: number
}

export type TRouteThemeLabelCounts = readonly IRouteThemeLabelCount[]

export interface IBaseLabel {
  readonly labelType: TLabelType
  readonly parentLabelId: number | null
}

export type TBaseLabels = readonly IBaseLabel[]

export interface ILabel extends IBaseLabel {
  /** Az egyedi azonosító. */
  readonly id: number
  /** A létrehozás időbélyege. */
  readonly createdAt: string
  /** Az adott platformon felvett név. */
  readonly name: string
  /** A szülő-címke neve az adott platformon. */
  readonly parentLabelName: string | null

  /** Az alábbi mezők bizonyos címketípusok esetében ott lesz. */
  /** Az adott címke évfolyama. */
  readonly grade: TChildrenGrade | null
  /** Az adott címke időszaka. */
  readonly period: TYearPeriod | null
  /** Az adott címke nehézsége. */
  readonly difficulty: TDifficulty | null
}

export type TLabels = readonly ILabel[]

export interface IGetLabelsResponse {
  readonly labels: TLabels
}

/** Címkékhez tartozó fordítások. */
export interface ILabelTranslation {
  readonly labelId: number
  readonly platform: TAvailablePlatform
  readonly translation: string
}

export type TLabelTranslations = readonly ILabelTranslation[]

export interface IInsertLabelRequest extends IBaseLabel {
  readonly translations: TLabelTranslation
}

export interface IUpdateLabelRequest extends IInsertLabelRequest {}

export interface IGetLabelByIdResponse {
  /** A címke adatai. */
  readonly label: ILabel
  /** A lehetséges szülőcímkék. */
  readonly possibleParentLabels: TLabels
  /** A címke fordításai az adott platformokon. */
  readonly translations: TLabelTranslations
}

export interface IGrouppedLabelType {
  readonly labelType: TLabelType
  readonly labelName: string
  readonly labels: TMutable<TLabels>
}

export type TGrouppedLabelTypes = readonly IGrouppedLabelType[]

export interface IGetLabelsEditorDataResponse {
  readonly labels: TGrouppedLabelTypes
}

/** Modulkeresőnél használt címkék. */
export interface IModuleFinderGetLabelsResponse {
  readonly moduleCount: number
  readonly labels: TGrouppedLabelTypes
}

/** Címkézés. */
export interface ILabelingRequest {
  /** A címkék azonosítói. */
  readonly labelIds: readonly number[]
  /** Évfolyamnehézség azonosítók. */
  readonly gradeDifficultyIds: readonly number[] // Ezt bele lehetne olvasztni a fentibe!
  /** OptionKey, ha van. */
  readonly optionKey: string | null
  /** Modulazonosító, ha van. */
  readonly moduleId: number | null
  /** Szintopció azonosító, ha van. */
  readonly levelOptionId: number | null
}

/** Útvonalcímkék. */
export interface IRouteLabel {
  /** Az útvonal azonosítója. */
  readonly routeId: number
  /** A címke azonosítója. */
  readonly labelId: number
}

export type TRouteLabels = readonly IRouteLabel[]

/** Modulcímkék. */
export interface IBaseModuleLabel {
  readonly moduleId: number
  readonly labelId: number
}

export type TBaseModuleLabels = readonly IBaseModuleLabel[]

export interface IModuleLabel extends IBaseModuleLabel {
  readonly createdAt: string
}

export type TModuleLabels = readonly IModuleLabel[]

export interface ILabelTypesByModuleId {
  /** A modul azonosítója. */
  readonly moduleId: number
  /** A címke típusa. */
  readonly labelType: TLabelType
  /** A címke megnevezése. */
  readonly labelName: string
  /** A címke azonosítója. */
  readonly labelId: number
}

export type TLabelTypesByModuleIds = readonly ILabelTypesByModuleId[]

export interface IGradePeriod {
  /** Az azonosító. */
  readonly id: number
  /** Évfolyam. */
  readonly grade: TChildrenGrade
  /** Év időszak. */
  readonly period: TYearPeriod
}

export type TGradePeriods = readonly IGradePeriod[]

export interface ITranslatedGradePeriod extends IGradePeriod {
  readonly id: number
  readonly name: string
}

export type TTranslatedGradePeriods = readonly ITranslatedGradePeriod[]

export interface IBaseGradeDifficulty {
  readonly grade: TChildrenGrade
  readonly difficulty: TDifficulty
}

export interface IRouteGradePeriod extends ITranslatedGradePeriod {
  readonly routeId: number
}

export type TRouteGradePeriods = readonly IRouteGradePeriod[]

export interface IGetGradePeriodsResponse {
  readonly gradePeriods: TTranslatedGradePeriods
}

export interface IGradeDifficulty extends IBaseGradeDifficulty {
  readonly name: string
}

export type TGradeDifficulties = readonly IGradeDifficulty[]

export interface IModuleGradeDifficulty extends IBaseGradeDifficulty {
  readonly moduleId: number
}

export type TModuleGradeDifficulties = readonly IModuleGradeDifficulty[]
